<script lang="ts">
	import OAuthLoginLayout from './routes/OAuthLoginLayout.svelte';
	import CredentialsStep from './routes/CredentialsStep.svelte';
	import HomePickerStep from './routes/HomePickerStep.svelte';
	import ConsentModal from './routes/ConsentModal.svelte';
	import { Text, Theme } from '@Shelly/shelly-design-system';
	import { _ } from 'svelte-i18n';
	import {
		buildErrorRedirectUrl,
		buildRedirectUrl,
		parseOAuthParams,
	} from './lib/oauth-params';
	import {
		discoverUnits,
		oauthLogin,
		type AvailableUnits,
	} from './services/auth';
	import { isNonRecoverable, oauthErrorCodeFor, type ErrorCode } from './lib/errors';
	import { PROVIDERS, SHELLY_MANAGE_INTEGRATIONS_URL, type Provider } from './lib/provider';

	interface Props {
		provider?: Provider;
	}

	const { provider = 'shelly' }: Props = $props();

	const providerConfig = $derived(PROVIDERS[provider]);

	type Step = 'credentials' | 'picker' | 'consent' | 'redirecting';

	const rawParamsResult = parseOAuthParams();

	// Dev convenience: when running under `vite dev` without OAuth handshake
	// params in the URL, fall back to stub values so the form is usable for
	// testing the credentials -> picker flow. The final /oauth/login call will
	// fail with `unauthorized_client` against the real backend, which is the
	// expected and useful error to exercise. Production builds (and dev builds
	// loaded from a production host) always enforce the contract.
	const devStub = import.meta.env.DEV && !rawParamsResult.ok;
	const paramsResult = devStub
		? ({
				ok: true,
				params: {
					clientId: 'dev-stub-client',
					redirectUri: 'https://example.com/cb',
					state: 'dev-state',
					responseType: 'code',
					scope: '',
				},
			} as const)
		: rawParamsResult;

	if (devStub) {
		console.warn(
			'[oauth-pages-v3] DEV stub OAuth params in use — /oauth/login will reject with unauthorized_client. Add real ?client_id=...&redirect_uri=... to exercise the full success path.',
		);
	}

	let step = $state<Step>('credentials');
	let email = $state('');
	let password = $state('');
	let availableUnits = $state<AvailableUnits>({});
	let loading = $state(false);
	let error = $state<ErrorCode | null>(null);
	// Unit id queued for the next /oauth/login call. Set when we transition
	// into the 'consent' step (single-unit auto-pick or after the user picks
	// in the multi-unit picker). `undefined` means "let /oauth/login default
	// to the first owned unit" (legacy single-unit accounts).
	let pendingUnit = $state<number | undefined>(undefined);

	const fatalReason = paramsResult.ok ? null : paramsResult.reason;

	const pendingUnitName = $derived(
		pendingUnit !== undefined ? availableUnits[pendingUnit] : undefined,
	);

	async function handleCredentialsSubmit() {
		if (!paramsResult.ok) return;
		loading = true;
		error = null;

		const discovery = await discoverUnits(email, password);
		if (discovery.kind === 'error') {
			error = discovery.code;
			loading = false;
			return;
		}

		if (discovery.kind === 'multi') {
			const filtered = filterOwnedUnits(discovery.availableUnits);
			const count = Object.keys(filtered).length;
			if (count > 1) {
				availableUnits = filtered;
				step = 'picker';
				loading = false;
				return;
			}
			if (count === 1) {
				availableUnits = filtered;
				const onlyId = Number(Object.keys(filtered)[0]);
				queueUnitOrIssue(onlyId);
				return;
			}
			// zero owned units after filtering — fall through to single-unit path
		}

		queueUnitOrIssue(undefined);
	}

	async function handlePickerSubmit(unitId: number) {
		loading = true;
		error = null;
		queueUnitOrIssue(unitId);
	}

	/**
	 * Either jump straight to /oauth/login (no-consent providers like the
	 * generic Shelly page) or stop on the consent step so the user can
	 * Accept/Deny first (Google etc.).
	 */
	function queueUnitOrIssue(unit: number | undefined) {
		pendingUnit = unit;

		if (providerConfig.requiresConsent) {
			step = 'consent';
			loading = false;
			return;
		}

		void issueOAuthCode(unit);
	}

	async function issueOAuthCode(unit: number | undefined) {
		if (!paramsResult.ok) return;
		const fromConsent = step === 'consent';
		loading = true;
		error = null;

		const result = await oauthLogin({
			email,
			password,
			clientId: paramsResult.params.clientId,
			responseType: paramsResult.params.responseType,
			unit,
		});

		if (result.kind === 'error') {
			// Non-recoverable errors from the consent step abandon the
			// in-page flow and redirect back to the third party with an
			// OAuth-spec `error` param. Google's handler then drives the
			// retry UX (vs us showing a dead-end in-modal alert).
			// Recoverable errors stay in-modal so the user can retry.
			if (fromConsent && isNonRecoverable(result.code)) {
				step = 'redirecting';
				window.location.replace(
					buildErrorRedirectUrl(
						paramsResult.params.redirectUri,
						oauthErrorCodeFor(result.code),
						paramsResult.params.state,
					),
				);
				return;
			}
			error = result.code;
			loading = false;
			return;
		}

		step = 'redirecting';
		window.location.replace(
			buildRedirectUrl(
				paramsResult.params.redirectUri,
				result.code,
				paramsResult.params.state,
			),
		);
	}

	function handleConsentAccept() {
		void issueOAuthCode(pendingUnit);
	}

	function handleConsentDeny() {
		if (!paramsResult.ok) return;
		// OAuth 2.0 §4.1.2.1: on denied authorization the AS redirects back
		// to redirect_uri with `error=access_denied` and echoes `state`.
		step = 'redirecting';
		window.location.replace(
			buildErrorRedirectUrl(
				paramsResult.params.redirectUri,
				'access_denied',
				paramsResult.params.state,
			),
		);
	}

	/**
	 * "Use a different account" from the consent modal. Per Google's
	 * account-linking design checklist, users with multiple accounts must
	 * have a way to switch without going through Deny (which would error
	 * back to the third-party app). Returns to the credentials step with
	 * a clean slate.
	 */
	function handleConsentSwitchAccount() {
		step = 'credentials';
		email = '';
		password = '';
		availableUnits = {};
		pendingUnit = undefined;
		error = null;
	}

	function handleBack() {
		step = 'credentials';
		pendingUnit = undefined;
		error = null;
	}

	/**
	 * `availableUnits` from /auth/login may include sub-unit ids in some accounts.
	 * /oauth/login only accepts owned units (in pkey.id) — picking a sub returns
	 * `invalid_unit`. Filtering owned vs. sub is server-side knowledge we don't
	 * have here, so the pass-through is intentional: the OAuth error from the
	 * second call surfaces an actionable message.
	 *
	 * If we later expose a `get_units_info`-backed enrichment, swap this in.
	 */
	function filterOwnedUnits(units: AvailableUnits): AvailableUnits {
		return units;
	}
</script>

<Theme>
	{#if devStub}
		<div class="dev-stub-banner" role="status">
			<strong>DEV</strong>
			using stub OAuth params (<code>client_id=dev-stub-client</code>). The final
			<code>/oauth/login</code> call will fail with <code>unauthorized_client</code>. Add
			real handshake params to the URL to test the full success path.
		</div>
	{/if}
	<OAuthLoginLayout>
		{#if step === 'redirecting'}
			<div class="redirecting">
				<Text element="h2">{$_('redirecting')}</Text>
			</div>
		{:else}
			<CredentialsStep
				bind:email
				bind:password
				loading={loading && step === 'credentials'}
				error={step === 'credentials' ? error : null}
				{fatalReason}
				providerName={providerConfig.requiresConsent ? providerConfig.displayName : undefined}
				onsubmit={handleCredentialsSubmit}
			/>
		{/if}
	</OAuthLoginLayout>

	{#if step === 'picker'}
		<HomePickerStep
			show
			{email}
			{availableUnits}
			{loading}
			{error}
			onpick={handlePickerSubmit}
			onback={handleBack}
		/>
	{/if}

	{#if step === 'consent'}
		<ConsentModal
			show
			providerName={providerConfig.displayName}
			homeName={pendingUnitName}
			{loading}
			{error}
			privacyPolicyUrl={providerConfig.privacyPolicyUrl}
			manageLinksUrl={SHELLY_MANAGE_INTEGRATIONS_URL}
			scope={paramsResult.ok ? paramsResult.params.scope : ''}
			onaccept={handleConsentAccept}
			ondeny={handleConsentDeny}
			onswitch={handleConsentSwitchAccount}
		/>
	{/if}
</Theme>

<style>
	.redirecting {
		text-align: center;
		padding: 32px;
	}
	.dev-stub-banner {
		position: fixed;
		top: 0;
		left: 0;
		right: 0;
		z-index: 9999;
		background: #ffb300;
		color: #1a1a1a;
		padding: 8px 16px;
		font-size: 12px;
		line-height: 1.5;
		text-align: center;
		box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.15);
	}
	.dev-stub-banner code {
		background: rgba(0, 0, 0, 0.12);
		padding: 1px 5px;
		border-radius: 3px;
		font-family: 'SF Mono', ui-monospace, Menlo, monospace;
	}
	.dev-stub-banner strong {
		font-weight: 700;
		letter-spacing: 0.05em;
		margin-right: 8px;
	}
</style>
